import React from 'react';
import { FormattedMessage } from 'react-intl';
import { InboxOutlined, SearchOutlined, WarningOutlined } from '@ant-design/icons';
import styled from 'types/theme.types';
import { typeStyle } from 'constants/typography';

export const tableEmptyStateTypes = ['no-data', 'no-results', 'error'] as const;
export type TableEmptyStateType = (typeof tableEmptyStateTypes)[number];

// Figma Table page nodes 304:161 / 304:167 / 304:174. Icons, not the isometric illustrations — the
// Illustrations page and the Table page disagree and this follows the one that draws a table.
// No `isNewDesign` branch: only the new-design path renders it.
const ICONS: Record<TableEmptyStateType, React.ReactNode> = {
  'no-data': <InboxOutlined />,
  'no-results': <SearchOutlined />,
  error: <WarningOutlined />
};

type TableEmptyStateProps = {
  type: TableEmptyStateType;
  /** `null` omits the title. */
  titleId?: string | null;
  description?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
  'data-e2e'?: string;
};

export const TableEmptyState: React.FunctionComponent<TableEmptyStateProps> = ({
  type,
  titleId,
  description,
  action,
  className,
  'data-e2e': dataE2e
}) => (
  <Container className={className} data-e2e={dataE2e}>
    <IconContainer isError={type === 'error'} data-e2e={dataE2e && `${dataE2e}-icon`}>
      {ICONS[type]}
    </IconContainer>
    {titleId === null ? null : (
      <Title>
        <FormattedMessage id={titleId ?? `table.empty.${type}.title`} />
      </Title>
    )}
    <Description>{description ?? <FormattedMessage id={`table.empty.${type}.description`} />}</Description>
    {action}
  </Container>
);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: ${({ theme }) => theme.spacing.xxxs};
  padding: ${({ theme }) => theme.padding.xxxl} 0;
`;

const IconContainer = styled.div<{ isError: boolean }>`
  display: flex;
  margin-bottom: ${({ theme }) => theme.spacing.md};

  svg {
    font-size: 32px;
    color: ${({ isError }) => (isError ? 'var(--icon-error)' : 'var(--icon-secondary)')};
  }
`;

const Title = styled.div`
  ${typeStyle.headingH4}
  color: var(--text-heading);
`;

const Description = styled.div`
  ${typeStyle.bodyMd}
  color: var(--text-secondary);
`;

export default TableEmptyState;
